import { useEffect, useState } from 'react'
import { NavLink, Link } from 'react-router-dom'
import {
  MessageSquare, BookOpen, FileText, Palette,
  BarChart2, Settings,
  Stethoscope, LogOut, Mic, FolderOpen, Shield,
  MessageCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { cerrarSesion } from '@/lib/supabase'
import { useBilling } from '@/hooks/useBilling'
import { getBillingPlanLabel } from '@/features/billing/billingPresentation'
import { useAuth } from '@/hooks/useAuth'
import { deriveAdminMode, getAdminModeSidebarLabel, obtenerAdminScope } from '@/lib/adminScope'
import { MPRCrosshair } from '@/components/ui/RadCoreLogo'
import { AppFeedbackDrawer } from '@/features/feedback/AppFeedbackDrawer'
import { getSidebarBrandPresentation } from './sidebarPresentation'
import type { Plan } from '@/types'
import toast from 'react-hot-toast'

const WORKFLOW_ITEMS = [
  { to: '/historial',   icono: FileText,      etiqueta: 'Historial',   tourId: 'sidebar-historial' },
  { to: '/casos',       icono: FolderOpen,    etiqueta: 'Casos',       tourId: 'sidebar-casos' },
  { to: '/patrones',    icono: BookOpen,      etiqueta: 'Patrones',    tourId: 'sidebar-patrones' },
  { to: '/chat',        icono: MessageSquare, etiqueta: 'RadCore AI',  tourId: 'sidebar-chat' },
]

const TOOL_ITEMS = [
  { to: '/rendimiento',     icono: BarChart2,   etiqueta: 'Rendimiento',      tourId: 'sidebar-rendimiento' },
  { to: '/clasificaciones', icono: Stethoscope, etiqueta: 'Clasificaciones',  tourId: 'sidebar-clasificaciones' },
  { to: '/estilo',          icono: Palette,     etiqueta: 'Mi Estilo',        tourId: 'sidebar-estilo' },
]

interface SidebarProps {
  nombreUsuario?: string
  plan?: Plan
}

function NavItem({
  to,
  icono: Icono,
  etiqueta,
  tourId,
}: {
  to: string
  icono: React.ElementType
  etiqueta: string
  tourId?: string
}) {
  return (
    <NavLink
      to={to}
      data-tour={tourId}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-sm border-l-2 pl-2.5 pr-2 py-[7px] transition-colors duration-100',
          isActive
            ? 'border-white/35 bg-white/[0.05] text-white'
            : 'border-transparent text-white/38 hover:text-white/68 hover:bg-white/[0.03]'
        )
      }
    >
      <Icono className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.6} />
      <span className="truncate text-[12.5px] font-medium">{etiqueta}</span>
    </NavLink>
  )
}

function FeedbackNavItem({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 rounded-sm border-l-2 pl-2.5 pr-2 py-[7px] transition-colors duration-100',
        'border-transparent text-white/38 hover:text-white/68 hover:bg-white/[0.03]'
      )}
    >
      <MessageCircle className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.6} />
      <span className="truncate text-[12.5px] font-medium">Feedback</span>
    </button>
  )
}

export function Sidebar({ nombreUsuario }: SidebarProps) {
  const { usuario } = useAuth()
  const { status: billingStatus } = useBilling()
  const [puedeVerAdmin, setPuedeVerAdmin] = useState(false)
  const [adminNavLabel, setAdminNavLabel] = useState<string | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  useEffect(() => {
    if (!usuario) {
      setPuedeVerAdmin(false)
      setAdminNavLabel(null)
      return
    }
    if (usuario.rol === 'admin') {
      setPuedeVerAdmin(true)
      setAdminNavLabel(getAdminModeSidebarLabel('platform'))
      return
    }
    let cancelado = false
    obtenerAdminScope()
      .then(scope => {
        if (cancelado) return
        const mode = deriveAdminMode(scope)
        setPuedeVerAdmin(scope.canOpenAdmin)
        setAdminNavLabel(getAdminModeSidebarLabel(mode))
      })
      .catch(() => {
        if (!cancelado) {
          setPuedeVerAdmin(false)
          setAdminNavLabel(null)
        }
      })
    return () => { cancelado = true }
  }, [usuario])

  const manejarCerrarSesion = async () => {
    try { await cerrarSesion() }
    catch { toast.error('Error al cerrar sesion') }
  }
  const brand = getSidebarBrandPresentation()

  return (
    <aside
      className="flex h-full w-[200px] select-none flex-col border-r border-white/[0.05] bg-[#0d1117]"
    >
      {/* Logo */}
      <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-white/[0.05] px-3 py-3">
        <div
          role="img"
          aria-label={brand.title}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-teal-300/15 bg-[radial-gradient(circle_at_35%_30%,rgba(45,212,191,0.18),rgba(255,255,255,0.035)_48%,rgba(0,0,0,0)_72%)] shadow-sm shadow-teal-950/20"
        >
          <MPRCrosshair size={19} color="rgb(153 246 228)" className="opacity-90" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-[13px] font-semibold text-white/85 tracking-[-0.01em]">{brand.title}</span>
          <span className="text-[9.5px] text-teal-100/38 font-semibold tracking-[0.12em] uppercase">{brand.subtitle}</span>
        </div>
      </div>

      {/* New Report CTA */}
      <div className={cn('flex-shrink-0 px-2 pt-3 pb-1')}>
        <Link
          to="/reporte"
          data-tour="sidebar-nuevo-reporte"
          className={cn(
            'flex items-center gap-2 rounded-md transition-colors duration-100',
            'bg-white/[0.07] hover:bg-white/[0.11] border border-white/[0.09]',
            'text-white/85 font-semibold',
            'px-2.5 py-2'
          )}
        >
          <Mic className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.6} />
          <span className="text-[12.5px] truncate">Reporte</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5">
        {/* Workflow group */}
        {WORKFLOW_ITEMS.map(item => (
          <NavItem key={item.to} {...item} />
        ))}

        {/* Divider */}
        <div className="my-1.5 ml-2.5 mr-2 h-px bg-white/[0.05]" />

        {/* Tools group */}
        {TOOL_ITEMS.map(item => (
          <NavItem key={item.to} {...item} />
        ))}
        <FeedbackNavItem onClick={() => setFeedbackOpen(true)} />
      </nav>

      {/* Footer */}
      <div className="border-t border-white/[0.05] py-2 px-2 flex flex-col gap-0.5 flex-shrink-0">
        {puedeVerAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-sm transition-colors',
                'pl-2.5 pr-2 py-[7px] border-l-2',
                isActive
                  ? 'border-amber-500/40 bg-white/[0.03] text-amber-300/80'
                  : 'border-transparent text-amber-500/45 hover:text-amber-400/65 hover:bg-white/[0.03]'
              )
            }
          >
            <Shield className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.6} />
            <span className="text-[12.5px] font-medium truncate">{adminNavLabel ?? 'Admin'}</span>
          </NavLink>
        )}

        <NavLink
          to="/ajustes"
          data-tour="sidebar-ajustes"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-sm transition-colors',
              'pl-2.5 pr-2 py-[7px] border-l-2',
              isActive
                ? 'border-white/30 bg-white/[0.04] text-white/80'
                : 'border-transparent text-white/30 hover:text-white/58 hover:bg-white/[0.03]'
            )
          }
        >
          <Settings className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.6} />
          <span className="text-[12.5px] font-medium truncate">Ajustes</span>
        </NavLink>

        {/* User card */}
        <div className="flex items-center gap-2 px-2 py-2 mt-1 rounded-md bg-white/[0.02]">
          <div className="w-6 h-6 rounded-full bg-white/[0.08] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-bold text-white/50 uppercase">
              {nombreUsuario?.[0] ?? '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-white/55 truncate leading-tight">
              {nombreUsuario ?? 'Radiologo'}
            </p>
            <Link to="/ajustes?tab=suscripcion" className="text-[10px] text-gray-400 hover:underline">
              {getBillingPlanLabel(billingStatus)}
            </Link>
          </div>
          <button
            onClick={manejarCerrarSesion}
            title="Cerrar sesion"
            className="p-1 text-white/18 hover:text-white/55 transition-colors"
          >
            <LogOut className="w-3 h-3" strokeWidth={1.6} />
          </button>
        </div>

      </div>
      <AppFeedbackDrawer open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </aside>
  )
}
